package design

import org.jetbrains.compose.web.css.*

/**
 * Design Tokens for Schneeblume
 *
 * This file contains all design tokens for the application including colors,
 * typography, spacing, shadows, transitions, and z-index values.
 *
 * Design Philosophy:
 * - Type Scale: 1.250 (Major Third) for harmonious sizing
 * - Spacing Base Unit: 4px for consistent rhythm
 * - Border Radius: 4px base for modern feel
 * - Shadows: Subtle elevation for depth
 */
object DesignTokens {
    /**
     * Color System
     *
     * Semantic colors with light/dark mode support.
     * Primary color: #4A90E2 (Blue)
     * Neutral palette: gray50 to gray900 (10 steps)
     */
    object Colors {
        // Primary colors
        val primary = Color("#4A90E2")
        val primaryDark = Color("#357ABD")
        val primaryLight = Color("#6AB0FF")
        val secondary = Color("#7B68EE")

        // Semantic colors
        val success = Color("#28A745")
        val warning = Color("#FFC107")
        val danger = Color("#DC3545")
        val info = Color("#17A2B8")

        // Neutral palette (10-step grayscale)
        val gray50 = Color("#F9FAFB")
        val gray100 = Color("#F3F4F6")
        val gray200 = Color("#E5E7EB")
        val gray300 = Color("#D1D5DB")
        val gray400 = Color("#9CA3AF")
        val gray500 = Color("#6B7280")
        val gray600 = Color("#4B5563")
        val gray700 = Color("#374151")
        val gray800 = Color("#1F2937")
        val gray900 = Color("#111827")

        // Semantic colors for light mode
        val background = Color("#FFFFFF")
        val surface = Color("#F9FAFB")
        val text = Color("#111827")
        val textSecondary = Color("#6B7280")
        val textMuted = Color("#9CA3AF")
        val border = Color("#E5E7EB")
        val focus = Color("#4A90E2")
        val focusRing = Color("rgba(74, 144, 226, 0.25)")

        // Legacy support (map to new system)
        val colorWhite = Color.white
        val colorPrimary = primary
        val colorDanger = danger
        val colorSuccess = success
        val colorWarning = warning
        val colorText = text
        val colorTextSecondary = textSecondary
        val colorTextMuted = textMuted
        val colorBorder = border
        val colorBackground = surface
    }

    /**
     * Typography System
     *
     * Type Scale: 1.250 (Major Third)
     * Font Sizes: 10px to 36px
     * Font Weights: 300 to 700
     * Line Heights: 1.25 to 1.75
     */
    object Typography {
        // Font sizes (Type scale: 1.250 - Major Third)
        val fontSizeXxs = 10.px   // 0.625rem
        val fontSizeXs = 12.px     // 0.75rem
        val fontSizeSm = 14.px    // 0.875rem
        val fontSizeBase = 16.px      // 1rem
        val fontSizeLg = 18.px    // 1.125rem
        val fontSizeXl = 20.px     // 1.25rem
        val fontSize2xl = 24.px     // 1.5rem
        val fontSize3xl = 30.px   // 1.875rem
        val fontSize4xl = 36.px    // 2.25rem

        // Font weights
        val fontWeightLight = 300
        val fontWeightNormal = 400
        val fontWeightMedium = 500
        val fontWeightSemibold = 600
        val fontWeightBold = 700

        // Line heights
        val lineHeightTight = 1.25
        val lineHeightNormal = 1.5
        val lineHeightRelaxed = 1.75

        // Font family stack
        val fontFamilySystem = listOf(
            "system-ui",
            "-apple-system",
            "BlinkMacSystemFont",
            "Segoe UI",
            "Roboto",
            "sans-serif"
        )
        val fontFamilyMono = listOf(
            "ui-monospace",
            "SFMono-Regular",
            "SF Mono",
            "Menlo",
            "Consolas",
            "monospace"
        )
    }

    /**
     * Spacing System
     *
     * Base Unit: 4px
     * Scale: 1, 2, 3, 4, 5, 6, 8, 10, 12, 16, 20
     * Values: 4px to 80px
     */
    object Spacing {
        val spacing0 = 0.px
        val spacing1 = 4.px   // 0.25rem
        val spacing2 = 8.px    // 0.5rem
        val spacing3 = 12.px   // 0.75rem
        val spacing4 = 16.px      // 1rem
        val spacing5 = 20.px   // 1.25rem
        val spacing6 = 24.px    // 1.5rem
        val spacing8 = 32.px      // 2rem
        val spacing10 = 40.px   // 2.5rem
        val spacing12 = 48.px     // 3rem
        val spacing16 = 64.px     // 4rem
        val spacing20 = 80.px     // 5rem
    }

    /**
     * Border Radius System
     *
     * Scale: 0, 2, 4, 6, 8, 12, 9999px
     */
    object BorderRadius {
        val none = 0.px
        val sm = 2.px    // 0.125rem
        val base = 4.px   // 0.25rem
        val md = 6.px    // 0.375rem
        val lg = 8.px      // 0.5rem
        val xl = 12.px     // 0.75rem
        val full = 9999.px
    }

    /**
     * Shadow System
     *
     * Five levels: sm, base, md, lg, xl
     * Plus inner shadow for focus states
     */
    object Shadows {
        val sm = "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
        val base = "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
        val md = "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
        val lg = "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
        val xl = "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"

        // Inner shadow for focus rings
        val inner = "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)"

        // Legacy support
        val shadowLight = sm
        val shadowMedium = base
    }

    /**
     * Transition System
     *
     * Durations: 150ms, 200ms, 300ms
     * Easing functions: default, in, out, in-out
     */
    object Transitions {
        // Durations
        val durationFast = 150.ms
        val durationBase = 200.ms
        val durationSlow = 300.ms

        // Easing functions
        val easingDefault = "cubic-bezier(0.4, 0, 0.2, 1)"
        val easingIn = "cubic-bezier(0.4, 0, 1, 1)"
        val easingOut = "cubic-bezier(0, 0, 0.2, 1)"
        val easingInOut = "cubic-bezier(0.4, 0, 0.6, 1)"

        // Common transitions
        val transitionBase = "all ${durationBase.value} ${easingDefault}"
        val transitionFast = "all ${durationFast.value} ${easingDefault}"
        val transitionSlow = "all ${durationSlow.value} ${easingDefault}"

        // Legacy support
        val transitionEase = "all 0.2s ease"
    }

    /**
     * Z-Index System
     *
     * Scale: 1000 to 1070
     * Ensures proper layering of modals, dropdowns, and tooltips
     */
    object ZIndex {
        val dropdown = 1000
        val sticky = 1020
        val fixed = 1030
        val modalBackdrop = 1040
        val modal = 1050
        val popover = 1060
        val tooltip = 1070
    }

    /**
     * Breakpoints
     *
     * Mobile-first responsive design breakpoints
     */
    object Breakpoints {
        val sm = 640.px   // Small screens (landscape phones)
        val md = 768.px   // Medium screens (tablets)
        val lg = 1024.px  // Large screens (desktops)
        val xl = 1280.px  // Extra large screens
        val xxl = 1536.px // Extra extra large screens
    }

    /**
     * Focus Styles
     *
     * WCAG 2.1 AA compliant focus indicators
     * Minimum 3:1 contrast ratio for focus indicators
     */
    object Focus {
        val width = 2.px
        val style = LineStyle.Solid
        val offset = 2.px
        val color = Colors.focus
        val colorRing = Colors.focusRing
    }

    /**
     * Touch Targets
     *
     * Minimum touch-friendly sizes for mobile
     */
    object Touch {
        val minSize = 44.px  // WCAG 2.5.5 recommendation
        val minSizeLarge = 48.px  // Enhanced touch target
    }

    /**
     * Utility functions for working with design tokens
     */
    object Utils {
        /**
         * Create a CSS custom property reference
         */
        fun varColor(name: String): CSSColorValue = Color("var(--$name)")

        /**
         * Get a shadow value as a CSS property
         */
        fun shadow(shadow: String): CSSStyleRuleBuilder.() -> Unit = {
            property("box-shadow", shadow)
        }

        /**
         * Get a transition value as a CSS property
         */
        fun transition(transition: String): CSSStyleRuleBuilder.() -> Unit = {
            property("transition", transition)
        }
    }
}
